import { examSourceEnums } from "src/constants/enums";
import {
  ExamSectionReportType,
  ExternalExamDataType,
  ProgressionExamSummaryType,
} from "src/services/exams/types";
import { asPercentage } from "src/utils/number/numberHelper";
import { getLocalDate } from "src/utils/date/dateHelper";

// The backend decides which exams are eligible and returns both sources in one
// shape, so the only thing left to tell apart here is where an exam was taken.
export const isRetinaLogikExam = (exam: ProgressionExamSummaryType) =>
  exam.source === examSourceEnums.RETINALOGIK;

export const countExamsForEye = (
  exams: Array<ProgressionExamSummaryType> | undefined,
  eye: string
) => (exams ?? []).filter((exam) => exam.eye === eye).length;

// True when an exam falls on an earlier day than the earliest GPA baseline
// exam, in the clinic's timezone. GPA leaves those exams out entirely, so they
// never get a visit. Same-day exams don't count as before the baseline.
export const isBeforeBaselineDay = (
  examDate: string | null | undefined,
  baselineDate: string | null | undefined,
  timezone: string | null
) =>
  Boolean(examDate && baselineDate) &&
  // yyyy-MM-dd strings sort in date order
  getLocalDate(examDate as string, timezone) <
    getLocalDate(baselineDate as string, timezone);

// Formats an MD or PSD value for display, e.g. -3.456 -> "-3.46 dB"
export const asDecibels = (value: number | null | undefined) =>
  value !== null && value !== undefined && !isNaN(Number(value))
    ? `${Number(value).toFixed(2)} dB`
    : "N/A";

// The six stats shown for each exam in a progression analysis. The keys double
// as their translation keys, and this is the order they are listed in.
export const PROGRESSION_STAT_KEYS = [
  "FL",
  "FN",
  "FP",
  "PSD",
  "MD",
  "VRFI",
] as const;

export type ProgressionStatKey = (typeof PROGRESSION_STAT_KEYS)[number];

export type ProgressionStatsType = Record<ProgressionStatKey, string>;

// At or below this MD, a VRFI of 0 isn't a real measurement
const MIN_MD_FOR_ZERO_VRFI = -25;

// True when a RetinaLogik exam's VRFI should read as N/A: an MD of -25 dB or
// lower with a VRFI of 0 means the field was too damaged to measure, not 0%
export const isVrfiNotApplicable = (
  totalMeanDeviation: number | null | undefined,
  retinaLogikFieldIndex: number | null | undefined
) =>
  typeof totalMeanDeviation === "number" &&
  totalMeanDeviation <= MIN_MD_FOR_ZERO_VRFI &&
  retinaLogikFieldIndex === 0;

// A RetinaLogik exam's stats, formatted for display. The report keeps them as
// the backend sends them: percentages as 0-1 fractions, MD and PSD in dB.
export const getExamStats = (
  reportData: ExamSectionReportType | undefined
): ProgressionStatsType => ({
  FL: asPercentage(reportData?.stats?.fixationLossPercentage),
  FN: asPercentage(reportData?.stats?.falseNegativePercentage),
  FP: asPercentage(reportData?.stats?.falsePositivePercentage),
  PSD: asDecibels(reportData?.postProcessing?.patternStandardDeviation),
  MD: asDecibels(reportData?.postProcessing?.totalMeanDeviation),
  VRFI: isVrfiNotApplicable(
    reportData?.postProcessing?.totalMeanDeviation,
    reportData?.postProcessing?.retinaLogikFieldIndex
  )
    ? "N/A"
    : asPercentage(reportData?.postProcessing?.retinaLogikFieldIndex, 0),
});

// An external exam's stats, formatted for display; VRFI is its VFI
export const getExternalExamStats = (
  data: ExternalExamDataType | undefined
): ProgressionStatsType => ({
  FL: asPercentage(data?.fixationLoss),
  FN: asPercentage(data?.falseNegative),
  FP: asPercentage(data?.falsePositive),
  PSD: asDecibels(data?.patternStandardDeviation),
  MD: asDecibels(data?.meanDeviation),
  VRFI: asPercentage(data?.visualFieldIndex),
});
